import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Quick view content, shared by every card so a product is only fetched once.
 * @type {Map<string, Promise<Element | null>>}
 */
const quickViewRequests = new Map();

/** @type {Set<string>} */
const quickViewReady = new Set();

let latestQuickViewRequest = 0;

const QUICK_VIEW_SKELETON = `
  <div class="sofario-qv-skeleton" aria-hidden="true">
    <div class="sofario-shimmer sofario-qv-skeleton__image"></div>
    <div class="sofario-qv-skeleton__details">
      <div class="sofario-shimmer sofario-qv-skeleton__line" style="--sofario-w: 85%; --sofario-h: 26px"></div>
      <div class="sofario-shimmer sofario-qv-skeleton__line" style="--sofario-w: 40%; --sofario-h: 22px"></div>
      <div class="sofario-shimmer sofario-qv-skeleton__line" style="--sofario-w: 30%; margin-block-start: 12px"></div>
      <div class="sofario-shimmer sofario-qv-skeleton__line" style="--sofario-w: 70%; --sofario-h: 40px"></div>
      <div class="sofario-shimmer sofario-qv-skeleton__line" style="--sofario-w: 30%; margin-block-start: 12px"></div>
      <div class="sofario-qv-skeleton__dots">
        <span class="sofario-shimmer"></span><span class="sofario-shimmer"></span><span class="sofario-shimmer"></span>
        <span class="sofario-shimmer"></span><span class="sofario-shimmer"></span><span class="sofario-shimmer"></span>
      </div>
      <div class="sofario-shimmer sofario-qv-skeleton__line" style="--sofario-h: 48px; margin-block-start: 16px"></div>
    </div>
  </div>
`;

/**
 * Fetches the light `product.quick` template, about a tenth of the size of the full product page,
 * and starts loading its first photo so the picture is ready when the dialog fills.
 * @param {string} productPageUrl
 * @returns {Promise<Element | null>}
 */
function loadQuickView(productPageUrl) {
  const existing = quickViewRequests.get(productPageUrl);
  if (existing) return existing;

  const url = new URL(productPageUrl, window.location.origin);
  url.searchParams.set('view', 'quick');

  const request = fetch(url)
    .then(async (response) => {
      if (!response.ok) throw new Error(`Failed to fetch quick view: HTTP error ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const productGrid = html.querySelector('[data-product-grid-content]');

      const firstImage = productGrid?.querySelector('img');
      if (firstImage instanceof HTMLImageElement) {
        const warmImage = new Image();
        warmImage.sizes = firstImage.sizes;
        warmImage.srcset = firstImage.srcset;
        warmImage.src = firstImage.src;
      }

      quickViewReady.add(productPageUrl);
      return productGrid;
    })
    .catch((error) => {
      quickViewRequests.delete(productPageUrl);
      throw error;
    });

  quickViewRequests.set(productPageUrl, request);
  return request;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {AbortController | null} */
  #prefetchAbortController = null;
  /** @type {number | undefined} */
  #prefetchTimer;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);

    // Start fetching on the way to a click, so most quick views open already filled.
    this.#prefetchAbortController = new AbortController();
    const { signal } = this.#prefetchAbortController;
    const hoverTarget = this.closest('product-card') ?? this;
    hoverTarget.addEventListener('pointerenter', this.#schedulePrefetch, { signal, passive: true });
    hoverTarget.addEventListener('pointerleave', this.#cancelPrefetch, { signal, passive: true });
    this.addEventListener('touchstart', this.#prefetch, { signal, passive: true });
    this.addEventListener('focusin', this.#prefetch, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    this.#prefetchAbortController?.abort();
    this.#cancelPrefetch();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  #schedulePrefetch = () => {
    this.#cancelPrefetch();
    this.#prefetchTimer = window.setTimeout(this.#prefetch, 120);
  };

  #cancelPrefetch = () => {
    window.clearTimeout(this.#prefetchTimer);
  };

  #prefetch = () => {
    if (!this.querySelector('.quick-add__button--choose')) return;
    const url = this.productPageUrl;
    if (url) loadQuickView(url).catch(() => {});
  };

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    quickViewRequests.clear();
    quickViewReady.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    const requestId = ++latestQuickViewRequest;
    const dialogComponent = document.getElementById('quick-add-dialog');
    const isReady = quickViewReady.has(currentUrl);

    // Content that is not ready yet opens as a placeholder, so a click always gets an instant response.
    // The dialog opens on the next frame, so closing is watched for rather than checking `open` later.
    let closedEarly = false;
    if (!isReady) {
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) modalContent.innerHTML = QUICK_VIEW_SKELETON;
      dialogComponent?.addEventListener(DialogCloseEvent.eventName, () => (closedEarly = true), { once: true });
      this.#openQuickAddModal();
    }

    let productGrid = null;
    try {
      productGrid = await loadQuickView(currentUrl);
    } catch (error) {
      console.warn('[quick-add] Could not load quick view:', error);
    }

    // A newer click, or the shopper closing the placeholder, replaces this request.
    if (requestId !== latestQuickViewRequest) return;
    if (closedEarly) return;

    if (!productGrid) {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Use a fresh clone from the cache
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);

    if (isReady) {
      this.#openQuickAddModal();
    } else {
      this.#resetScroll();
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#syncHeaderState(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  /**
   * Mirrors the header states that product media and details styles depend on. Those rules reach page
   * content from `#header-group` with a sibling combinator, which can't reach this body-level dialog.
   * Scroll is locked while the dialog is open, so the header can't change state until it closes.
   *
   * @param {QuickAddDialog} dialogComponent
   */
  #syncHeaderState(dialogComponent) {
    dialogComponent.toggleAttribute(
      'header-transparent',
      document.querySelector('header-component[transparent]') !== null
    );
    dialogComponent.toggleAttribute(
      'header-sticky-active',
      document.querySelector("#header-group #header-component[data-sticky-state='active']") !== null
    );
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      // The colour choice for older sofas and the cash on delivery note come along on phones too
      const colourChoice = productGrid.querySelector('.sofario-colour-choice');
      if (colourChoice) {
        productGrid.appendChild(colourChoice);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      const cashNote = productGrid.querySelector('.sofario-qv-note');
      if (cashNote) {
        productGrid.appendChild(cashNote);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
